import { useCallback, useEffect, useRef, useState, type MouseEvent, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion, useScroll, useSpring, type Variants } from 'framer-motion';
import { ArrowRight, ArrowUpRight, Coins, LogIn, LogOut, Search } from 'lucide-react';
import { BrandMark } from '@/components/layout/BrandMark';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/useAuth';
import { SupportDialog } from '@/components/support/SupportDialog';
import { LanguageFlagToggle } from '@/components/ui/LanguageFlagToggle';
import { Magnetic } from '@/components/fx/Magnetic';
import { GoogleSignInButton } from '@/components/auth/GoogleSignInButton';
import { FM_EASE_OUT } from '@/lib/motion';
import { useTranslation } from 'react-i18next';
import { editorLang, editorPath } from '@/components/editorPages/routes';
import { useAvatarEntry } from '@/components/facelock/useAvatarEntry';
import { StudioList } from '@/components/layout/StudioList';
import { openCommandMenu } from '@/components/layout/CommandMenu';

interface NavLink {
  path: string;
  hash: string;
  search?: string;
  label: string;
  authRequired?: boolean;
  /** Shown as a picture button instead of text (the label becomes its accessible name). */
  image?: NavPicture;
}

interface NavPicture {
  src: string;
  /** Intrinsic width at the 48px bar height, for layout before it loads. */
  width: number;
  testId: string;
  /** Hover glow, matched to the art's own colour. */
  glow: string;
}

// Nav text in rust orange (owner, 2026-09-24): the copper of the logo's
// CHOP SHOP and the ChopBox art, dark enough to read as rust. 6.2:1 on the
// navbar background; hover lifts to a lighter copper (9.5:1). Kept through
// the immersive redesign: the owner's order, not a style of the old bar.
const NAV_TEXT = 'text-[#D9773A] hover:text-[#F0A26B]';

// Every bar entry is a picture, not a word (owner, 2026-09-24 and 09-26).
// ChopBox: the clapperboard art, 255x192 (64x48 in the bar). Support,
// Pricing and FAQ: gold vector icon tiles, 120x96 (60x48 in the bar).
const GOLD_GLOW = 'group-hover/pic:shadow-[0_0_18px_-2px_rgba(244,190,82,0.6)]';
const CHOPBOX_PICTURE: NavPicture = {
  src: '/brand/chopbox-nav.webp',
  width: 64,
  testId: 'nav-chopbox',
  glow: 'group-hover/pic:shadow-[0_0_18px_-2px_rgba(75,203,190,0.65)]',
};
const PRICING_PICTURE: NavPicture = { src: '/brand/nav-pricing.svg', width: 60, testId: 'nav-pricing', glow: GOLD_GLOW };
const FAQ_PICTURE: NavPicture = { src: '/brand/nav-faq.svg', width: 60, testId: 'nav-faq', glow: GOLD_GLOW };
const SUPPORT_PICTURE: NavPicture = { src: '/brand/nav-support.svg', width: 60, testId: 'nav-support', glow: GOLD_GLOW };
const AVATARS_PICTURE: NavPicture = { src: '/brand/nav-avatars.svg', width: 60, testId: 'nav-avatars', glow: GOLD_GLOW };
// Quick tools (owner, 2026-10-16): a gold tile like the others, never a text link.
const QUICK_TOOLS_PICTURE: NavPicture = { src: '/brand/nav-quicktools.svg', width: 60, testId: 'nav-quick-tools', glow: GOLD_GLOW };

// WORDS UNDER EVERY PICTURE (Studio Blueprint 01/02, 2026-10-16): the bar
// keeps the owner's picture buttons and prints each one's name under it, so
// no control in the frame is an unlabelled icon.
const BAR_CAPTION = 'mt-1 block text-center text-[10.5px] font-semibold leading-none tracking-wide text-white/75 transition-colors group-hover/pic:text-[#F0A26B]';

const PICTURE_BUTTON = 'group/pic relative rounded-lg transition-transform duration-300 ease-expo-out';

function NavImage({ picture, className }: { picture: NavPicture; className: string }) {
  return (
    <img
      src={picture.src}
      alt=""
      width={picture.width}
      height={48}
      decoding="async"
      className={`w-auto shrink-0 rounded-lg shadow-[0_6px_18px_rgba(0,0,0,0.45)] transition-[filter,box-shadow,transform] duration-300 ease-expo-out group-hover/pic:brightness-110 ${picture.glow} ${className}`}
    />
  );
}

// The picture button: the label is its accessible name and tooltip. The bar
// shows the picture alone; the sheet rows draw the label beside it.
function ImageNavLink({ link, className = '', imgClassName, onClick, lead, trail }: {
  link: NavLink & { image: NavPicture };
  className?: string;
  imgClassName: string;
  onClick?: (e: MouseEvent<HTMLAnchorElement>) => void;
  /** Row furniture drawn around the picture (the sheet's index and arrow). */
  lead?: ReactNode;
  trail?: ReactNode;
}) {
  return (
    <Link
      to={{ pathname: link.path, search: link.search, hash: link.hash }}
      aria-label={link.label}
      title={link.label}
      onClick={onClick}
      data-testid={link.image.testId}
      className={`${PICTURE_BUTTON} ${className}`}
    >
      {lead}
      <NavImage picture={link.image} className={imgClassName} />
      {trail}
    </Link>
  );
}

const hasImage = (link: NavLink): link is NavLink & { image: NavPicture } => Boolean(link.image);

// The bar (immersive redesign, 2026-09-24):
//   * phones: logo, the compact ENG/ESP toggle and a 44px menu button — the
//     old bar cut the menu button off on a 320px iPhone SE;
//   * md–xl (tablets, small laptops): the same plus the Sign Up / Buy credits
//     CTA; the links live in the menu sheet;
//   * xl and up: the full row of links, magnetic CTAs.
// The full row only fits from 1280px once Spanish labels and a signed-in
// account are on it, so everything narrower gets the sheet.
//
// It is tall and clear over the hero at the top of the page and condenses
// (shorter, glass, hairline, shadow) once the page scrolls. Heights are in
// rem so they track the phone type scale in index.css, and the expanded
// height is 4rem: the other pages pad their <main> by pt-16 for it.
const EXPANDED = '[--nav-h:4rem]';
const CONDENSED = '[--nav-h:3.75rem] xl:[--nav-h:3.5rem]';

const sheetVariants: Variants = {
  closed: { clipPath: 'inset(0% 0% 100% 0%)' },
  open: { clipPath: 'inset(0% 0% 0% 0%)', transition: { duration: 0.7, ease: FM_EASE_OUT } },
  // pointer-events off for the exit, so a tap during the fold-up reaches the page.
  exit: { clipPath: 'inset(0% 0% 100% 0%)', pointerEvents: 'none', transition: { duration: 0.45, ease: [0.7, 0, 0.84, 0], delay: 0.05 } },
};

const listVariants: Variants = {
  closed: {},
  open: { transition: { staggerChildren: 0.06, delayChildren: 0.14 } },
  exit: { transition: { staggerChildren: 0.02, staggerDirection: -1 } },
};

const itemVariants: Variants = {
  closed: { opacity: 0, y: 28 },
  open: { opacity: 1, y: 0, transition: { duration: 0.8, ease: FM_EASE_OUT } },
  exit: { opacity: 0, y: -8, transition: { duration: 0.2 } },
};

// Reduced motion: the sheet and its rows simply fade.
const fadeVariants: Variants = {
  closed: { opacity: 0 },
  open: { opacity: 1, transition: { duration: 0.2 } },
  exit: { opacity: 0, pointerEvents: 'none', transition: { duration: 0.15 } },
};

// Sheet rows: at least 4rem, and taller on a tall phone (10% of the small
// viewport height, up to 5.25rem), so the list fills the sheet evenly.
// Plain 4rem where svh units are unknown (iOS < 15.4).
const ROW_HEIGHT = 'min-h-[4rem] supports-[height:1svh]:min-h-[max(4rem,min(10svh,5.25rem))]';

// Sheet pictures: 48px on a short phone (iPhone SE), so the row keeps 4rem;
// 56px where rows grow.
const SHEET_IMAGE = 'h-12 group-active/pic:scale-[0.97] [@media(min-height:640px)]:h-14';

const SHEET_LABEL = 'min-w-0 flex-1 font-display text-[length:clamp(1.35rem,6.4vw,2rem)] font-bold leading-[1.1] tracking-[-0.01em]';
// A picture row's label: the rust orange of the text rows, lit with the row.
const SHEET_PICTURE_LABEL = `${SHEET_LABEL} text-[#D9773A] transition-colors group-hover:text-[#F0A26B] group-active:text-[#F0A26B]`;

const FOCUSABLE ='a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])';

// A click with detail 0 came from the keyboard (Enter/Space on a button or
// link); a mouse or a finger always reports 1 or more.
const fromKeyboard = (e: { detail: number }) => e.detail === 0;

// Moves focus without drawing the focus ring. Used after a tap: iOS Safari
// treats programmatic focus that follows a (non-focusing) tap as keyboard
// focus and paints the square amber :focus-visible ring on it. `focusVisible`
// is the standard option (not yet in TypeScript's DOM types); where it is
// unsupported the target is a tabIndex=-1 container with no outline anyway.
const quietFocus = (el: HTMLElement | null | undefined) =>
  el?.focus({ preventScroll: true, focusVisible: false } as FocusOptions);

export function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { isAuthenticated, user, logout } = useAuth();
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const reduceMotion = useReducedMotion();
  // Avatars (2026-10-02): to the avatar section with Create avatar at its top,
  // only where the server says avatars are on (components/facelock/useAvatarEntry).
  const avatarEntry = useAvatarEntry(isAuthenticated);
  const avatarUrl = new URL(avatarEntry.href, 'https://site.invalid');
  const navRef = useRef<HTMLElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const sheetRef = useRef<HTMLDivElement>(null);
  // The support dialog opens on top of the sheet; while it is up, Esc and Tab
  // belong to the dialog, not to the sheet.
  const supportOpenRef = useRef(false);
  // Opened with Enter/Space: focus goes to the first link (a keyboard user
  // needs to see where they are). Opened by tap or click: focus goes to the
  // sheet itself, quietly, so no ring is drawn on "Pricing". (2026-09-24)
  const openedByKeyboardRef = useRef(false);

  // Pricing is LIVE again (MXN packs + Stripe checkout on /pricing), so it's
  // back in the nav — a user has to be able to find it. Public link so
  // logged-out visitors see prices too.
  // No Kids' Storybook entry (owner, 2026-09-26): the landing page lists it
  // with the other tools, and the footer links it.
  const navLinks: NavLink[] = [
    { path: '/pricing', hash: '', label: t('navigation.pricing'), image: PRICING_PICTURE },
    { path: '/', hash: '#faq', label: t('navigation.faq'), image: FAQ_PICTURE },
    // The pay-per-file quick tools (2026-10-16): their own page, as a gold tile.
    { path: '/tools', hash: '', label: t('quickTools.navLabel'), image: QUICK_TOOLS_PICTURE },
    ...(avatarEntry.show
      ? [{ path: avatarUrl.pathname, search: avatarUrl.search, hash: avatarUrl.hash, label: t('navigation.avatars'), image: AVATARS_PICTURE }]
      : []),
    // Signed in: the ChopShop (gallery + ChopBox). Signed out: the same ChopBox
    // art opens the free public editor (2026-09-29), in the site's language.
    isAuthenticated
      ? { path: '/chopshop', hash: '', label: t('navigation.chopShop'), authRequired: true, image: CHOPBOX_PICTURE }
      : { path: editorPath('hub', editorLang(i18n.language)), hash: '', label: t('navigation.freeEditor'), image: CHOPBOX_PICTURE },
  ];
  const visibleLinks = navLinks.filter((link) => !link.authRequired || isAuthenticated);

  const isCurrent = (link: NavLink) =>
    link.hash ? location.pathname === link.path && location.hash === link.hash : location.pathname === link.path;

  const credits = typeof user?.credits === 'number' ? user.credits : null;
  const creditsLabel = credits === null
    ? t('immersiveChrome.nav.yourCredits', { defaultValue: 'Your credits' })
    : t('immersiveChrome.nav.creditsCount', {
      count: credits,
      formatted: credits.toLocaleString(),
      defaultValue: '{{formatted}} credits',
    });

  // returnFocus: put focus back on the menu button. Only for keyboard closes
  // (Esc, Enter on a row or on the X): after a tap the ring it draws on iOS
  // is noise, and a touch user's place is wherever they tap next.
  const close = useCallback((returnFocus = true) => {
    setIsOpen(false);
    if (returnFocus) {
      // After the sheet's content is gone, so focus doesn't land in a node
      // that is about to unmount.
      requestAnimationFrame(() => menuButtonRef.current?.focus({ preventScroll: true }));
    }
  }, []);

  // Condense once the page has moved. rAF-throttled, passive.
  useEffect(() => {
    let raf = 0;
    const read = () => {
      raf = 0;
      setScrolled(window.scrollY > 12);
    };
    const onScroll = () => {
      if (!raf) raf = window.requestAnimationFrame(read);
    };
    read();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (raf) window.cancelAnimationFrame(raf);
    };
  }, []);

  // A route change (a link in the sheet, the browser's back button) closes it.
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname, location.hash]);

  // While the sheet is open: the page behind it can't scroll, Esc closes it,
  // Tab cycles inside the bar + sheet, and growing the window past the sheet's
  // breakpoint closes it so the scroll lock can't outlive it.
  useEffect(() => {
    if (!isOpen) return undefined;
    const html = document.documentElement;
    const body = document.body;
    const prev = { html: html.style.overflow, body: body.style.overflow, gutter: html.style.scrollbarGutter };
    // Keep the scrollbar's gutter while locked (a laptop under 1280px gets the
    // sheet too): no sideways jump, and no resize for ScrollTrigger to re-measure.
    html.style.scrollbarGutter = 'stable';
    html.style.overflow = 'hidden';
    body.style.overflow = 'hidden';

    const onKey = (e: KeyboardEvent) => {
      // The support dialog's own Esc runs first (capture phase) and marks the
      // event handled; that Esc closes the dialog only, not the sheet too.
      if (supportOpenRef.current || e.defaultPrevented) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        close(true);
        return;
      }
      if (e.key !== 'Tab' || !navRef.current) return;
      const items = Array.from(navRef.current.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
        (el) => el.getClientRects().length > 0 && getComputedStyle(el).visibility !== 'hidden',
      );
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      const activeEl = document.activeElement as HTMLElement | null;
      if (e.shiftKey && (activeEl === first || !navRef.current.contains(activeEl))) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (activeEl === last || !navRef.current.contains(activeEl))) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey);

    let mq: MediaQueryList | null = null;
    const onWide = (e: MediaQueryListEvent) => {
      if (e.matches) setIsOpen(false);
    };
    try {
      mq = window.matchMedia('(min-width: 1280px)');
      mq.addEventListener('change', onWide);
    } catch {
      mq = null; // jsdom: no matchMedia
    }

    // The page behind the opaque sheet is out of reach for screen readers
    // and Tab alike while it is open (inert). Only elements this effect
    // marked get unmarked, in case a page set inert on its own.
    const nav = navRef.current;
    const inerted = Array.from(document.querySelectorAll<HTMLElement>('main, footer')).filter(
      (el) => !el.hasAttribute('inert') && !(nav && (el.contains(nav) || nav.contains(el))),
    );
    inerted.forEach((el) => el.setAttribute('inert', ''));

    // Focus moves into the sheet: the first link after a keyboard open, the
    // sheet itself (no ring) after a tap or click.
    const focusTimer = window.setTimeout(() => {
      const sheet = sheetRef.current;
      if (!sheet) return;
      if (openedByKeyboardRef.current) sheet.querySelector<HTMLElement>(FOCUSABLE)?.focus({ preventScroll: true });
      else quietFocus(sheet);
    }, 60);

    return () => {
      html.style.overflow = prev.html;
      body.style.overflow = prev.body;
      html.style.scrollbarGutter = prev.gutter;
      inerted.forEach((el) => el.removeAttribute('inert'));
      document.removeEventListener('keydown', onKey);
      mq?.removeEventListener('change', onWide);
      window.clearTimeout(focusTimer);
    };
  }, [isOpen, close]);

  // Reading progress: a hairline along the bottom of the condensed bar.
  const { scrollYProgress } = useScroll();
  const smoothProgress = useSpring(scrollYProgress, { stiffness: 140, damping: 30, restDelta: 0.001 });
  const progress = reduceMotion ? scrollYProgress : smoothProgress;

  const condensed = scrolled && !isOpen;
  // Only the landing page has a dark hero to float over. Every other page
  // gets the bar from the top, and near-solid: over the cream storybook
  // pages see-through glass reads as a muddy grey band.
  const onLanding = location.pathname === '/';
  const glass = condensed || (!onLanding && !isOpen);
  const menuLabel = isOpen
    ? t('immersiveChrome.nav.closeMenu', { defaultValue: 'Close navigation menu' })
    : t('immersiveChrome.nav.openMenu', { defaultValue: 'Open navigation menu' });

  const sheetItem = reduceMotion ? fadeVariants : itemVariants;

  return (
    <nav
      ref={navRef}
      data-site-navbar
      data-condensed={condensed ? '' : undefined}
      aria-label={t('navigation.mainNavigation')}
      className={`fixed inset-x-0 top-0 z-50 h-[var(--nav-h)] transition-[height] duration-500 ease-expo-out ${
        condensed ? CONDENSED : EXPANDED
      }`}
    >
      {/* Bar surface. At the top of the page only a soft scrim (the hero's
          canvas shows through); once scrolled, dark glass with a hairline and
          a shadow. One backdrop-filter, on this layer only (iOS Safari
          stalls on stacked blurs). Denser and unsaturated on phones
          (2026-09-25): at 80% the cream storybook card came through as a
          flat grey band, and the saturation boost tinted it green over the
          ads card. */}
      <div
        aria-hidden="true"
        className={`pointer-events-none absolute inset-0 -z-10 transition-opacity duration-500 ease-expo-out ${
          isOpen ? 'opacity-0' : 'opacity-100'
        }`}
      >
        <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(5,7,10,0.72),rgba(5,7,10,0))]" />
        <div
          className={`absolute inset-0 shadow-[0_10px_30px_-12px_rgba(0,0,0,0.7)] backdrop-blur-xl backdrop-saturate-150 transition-opacity duration-500 ease-expo-out ${
            onLanding ? 'bg-[rgba(8,10,14,0.8)] max-md:bg-[rgba(8,10,14,0.9)] max-md:backdrop-saturate-100' : 'bg-[rgba(8,10,14,0.95)]'
          } ${
            glass ? 'opacity-100' : 'opacity-0'
          }`}
        />
        <div
          className={`absolute inset-x-0 bottom-0 h-px bg-[linear-gradient(90deg,transparent,rgba(255,255,255,0.14)_20%,rgba(255,255,255,0.14)_80%,transparent)] transition-opacity duration-500 ${
            glass ? 'opacity-100' : 'opacity-0'
          }`}
        />
        <motion.div
          className={`absolute inset-x-0 bottom-0 h-px origin-left bg-[linear-gradient(90deg,hsl(var(--primary)),hsl(var(--accent)))] transition-opacity duration-500 ${
            condensed ? 'opacity-80' : 'opacity-0'
          }`}
          style={{ scaleX: progress }}
        />
      </div>

      {/* On the page grid (2026-09-25): the sections' gutters (1rem, 2rem
          on tablets) and, from xl, their max-w-6xl content box (72rem + 2 ×
          1rem), so the logo and the CTA line up with the cards' edges
          instead of sitting ~33px outside them. The full link row gets at
          least 1224px from 1280 up (it had 1212 before). */}
      <div className="relative z-10 mx-auto flex h-full max-w-[74rem] items-center justify-between gap-1.5 px-2.5 min-[360px]:px-4 md:px-8 xl:px-4">
        {/* Logo */}
        <Link
          to="/"
          className="group -ml-1 flex min-h-[44px] min-w-0 shrink items-center rounded-xl p-1"
          onClick={() => isOpen && close(false)}
        >
          <BrandMark alt={t('navigation.logoAlt')} />
        </Link>

        {/* Desktop links (xl+): the picture buttons share the space between
            the logo and the right cluster evenly (owner, 2026-09-26). */}
        <div className="hidden min-w-0 flex-1 items-center justify-evenly xl:flex">
          <SupportDialog
            trigger={
              <button
                type="button"
                aria-label={t('support.title')}
                title={t('support.title')}
                data-testid={SUPPORT_PICTURE.testId}
                className={`${PICTURE_BUTTON} inline-flex flex-col items-center hover:-translate-y-0.5`}
              >
                <NavImage picture={SUPPORT_PICTURE} className="h-9" />
                <span aria-hidden="true" className={BAR_CAPTION}>{t('shell.supportShort')}</span>
              </button>
            }
          />
          {visibleLinks.map((link) => (
            hasImage(link) ? (
              <ImageNavLink
                key={`${link.path}${link.hash}`}
                link={link}
                className="inline-flex flex-col items-center hover:-translate-y-0.5"
                imgClassName="h-9"
                trail={<span aria-hidden="true" className={BAR_CAPTION}>{link.label}</span>}
              />
            ) : (
              <Link
                key={`${link.path}${link.hash}`}
                to={{ pathname: link.path, hash: link.hash }}
                aria-current={isCurrent(link) ? 'page' : undefined}
                className={`link-underline whitespace-nowrap rounded-lg px-3 py-2 text-[0.8rem] font-medium ${NAV_TEXT} transition-colors duration-300 [--underline-bottom:0.35rem] [--underline-inset:0.75rem] aria-[current=page]:text-[#F0A26B]`}
              >
                {link.label}
              </Link>
            )
          ))}
        </div>

        {/* Right cluster: language, account, menu button */}
        <div className="flex shrink-0 items-center gap-1 min-[360px]:gap-1.5 sm:gap-2.5">
          {/* Search (Studio Blueprint 03): the command menu, Ctrl K / Cmd K. */}
          <button
            type="button"
            onClick={openCommandMenu}
            data-testid="nav-search"
            className={`hidden h-10 items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-3.5 text-[0.78rem] font-semibold text-foreground transition-colors hover:border-white/20 hover:bg-white/[0.08] md:inline-flex ${isOpen ? 'md:invisible' : ''}`}
          >
            <Search className="h-4 w-4 text-primary" aria-hidden="true" />
            {t('shell.search')}
            <kbd className="hidden rounded border border-white/15 px-1.5 font-mono text-[10px] text-muted-foreground 2xl:inline">Ctrl K</kbd>
          </button>
          <LanguageFlagToggle
            compact
            className={`transition-opacity duration-300 ${isOpen ? 'pointer-events-none invisible opacity-0' : 'opacity-100'}`}
          />

          {isAuthenticated ? (
            <>
              {/* Live credit balance — links to the dashboard. */}
              <Link
                to="/chopshop"
                title={t('navigation.creditBalance')}
                className="hidden h-9 items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.04] px-3 text-[0.78rem] font-semibold tabular-nums text-foreground transition-colors hover:border-primary/40 hover:bg-white/[0.07] xl:flex"
              >
                <Coins className="h-4 w-4 text-primary" aria-hidden="true" />
                {credits === null ? '—' : credits.toLocaleString()}
              </Link>
              <Magnetic strength={0.3} radius={36} className={`hidden md:inline-block ${isOpen ? 'md:invisible' : ''}`}>
                <Link
                  to="/pricing"
                  className="group/cta relative inline-flex h-10 items-center gap-1.5 overflow-hidden whitespace-nowrap rounded-full bg-[linear-gradient(135deg,#F9D27E,#F4BE52_45%,#E4A43A)] px-4 text-[0.78rem] font-bold text-[#10131a] shadow-[0_8px_24px_-8px_rgba(244,190,82,0.65),inset_0_1px_0_rgba(255,255,255,0.45)] transition-[transform,box-shadow] duration-300 ease-expo-out hover:shadow-[0_12px_32px_-8px_rgba(244,190,82,0.8),inset_0_1px_0_rgba(255,255,255,0.5)] active:scale-[0.97]"
                >
                  {t('navigation.buyCreditsCta')}
                  <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 ease-expo-out group-hover/cta:translate-x-0.5" aria-hidden="true" />
                </Link>
              </Magnetic>
              <Button
                variant="ghost"
                size="sm"
                onClick={logout}
                title={t('auth.logout')}
                className={`hidden h-9 rounded-full px-3 ${NAV_TEXT} hover:bg-white/[0.06] xl:inline-flex`}
              >
                <LogOut aria-hidden="true" />
                {/* Icon only at every width (2026-09-25): on the 72rem grid the
                    Spanish row ("Cerrar Sesión") no longer fits with the
                    word shown. The title above is the tooltip. */}
                <span className="sr-only">{t('auth.logout')}</span>
              </Button>
            </>
          ) : (
            <>
              <Button
                asChild
                variant="ghost"
                size="sm"
                className={`hidden h-10 rounded-full px-3.5 text-[0.78rem] ${NAV_TEXT} hover:bg-white/[0.06] xl:inline-flex`}
              >
                <Link to="/login">
                  <LogIn aria-hidden="true" />
                  {t('auth.login')}
                </Link>
              </Button>
              {/* Google, icon only (2026-09-25): the row has no width left
                  for the words (see the logout note below). The menu
                  button's round control at 40px; the sheet has its own
                  full-width one, so this hides with Sign Up while it's open. */}
              <GoogleSignInButton
                variant="icon"
                source="navbar"
                className={`hidden border-white/10 bg-white/[0.04] shadow-none hover:border-white/20 hover:bg-white/[0.08] md:inline-flex ${isOpen ? 'md:invisible' : ''}`}
              />
              <Magnetic strength={0.3} radius={36} className={`hidden md:inline-block ${isOpen ? 'md:invisible' : ''}`}>
                <Link
                  to="/signup"
                  className="group/cta relative inline-flex h-10 items-center gap-1.5 overflow-hidden whitespace-nowrap rounded-full bg-[linear-gradient(135deg,#F9D27E,#F4BE52_45%,#E4A43A)] px-4 text-[0.78rem] font-bold text-[#10131a] shadow-[0_8px_24px_-8px_rgba(244,190,82,0.65),inset_0_1px_0_rgba(255,255,255,0.45)] transition-[transform,box-shadow] duration-300 ease-expo-out hover:shadow-[0_12px_32px_-8px_rgba(244,190,82,0.8),inset_0_1px_0_rgba(255,255,255,0.5)] active:scale-[0.97]"
                >
                  {t('auth.signup')}
                  <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 ease-expo-out group-hover/cta:translate-x-0.5" aria-hidden="true" />
                </Link>
              </Magnetic>
            </>
          )}

          {/* Menu button: two bars that cross into an X. 44x44 on every
              screen that shows it. */}
          <button
            ref={menuButtonRef}
            type="button"
            className="relative flex h-[44px] w-[44px] shrink-0 items-center justify-center rounded-full border border-white/10 bg-white/[0.04] text-foreground transition-[background-color,border-color,transform] duration-300 hover:border-white/20 hover:bg-white/[0.08] active:scale-95 xl:hidden"
            onClick={(e) => {
              if (isOpen) {
                close(fromKeyboard(e));
              } else {
                openedByKeyboardRef.current = fromKeyboard(e);
                setIsOpen(true);
              }
            }}
            aria-label={menuLabel}
            aria-expanded={isOpen}
            // Only while the sheet exists: aria-controls must name a node
            // that is in the document.
            aria-controls={isOpen ? 'site-menu-sheet' : undefined}
          >
            <span aria-hidden="true" className="relative block h-3 w-[18px]">
              <span
                className={`absolute left-0 h-[1.5px] w-full rounded-full bg-current transition-transform duration-500 ease-expo-out ${
                  isOpen ? 'top-[5.25px] rotate-45' : 'top-[1px]'
                }`}
              />
              <span
                className={`absolute left-0 h-[1.5px] rounded-full bg-current transition-[transform,width,top] duration-500 ease-expo-out ${
                  isOpen ? 'top-[5.25px] w-full -rotate-45' : 'top-[9.5px] w-[65%]'
                }`}
              />
            </span>
          </button>
        </div>
      </div>

      {/* The menu sheet (below xl): full height, under the bar, which stays
          on top with the logo and the close button. inset-0 is the fallback
          for browsers without dvh units. */}
      <AnimatePresence>
        {isOpen && (
          // A dialog, named "Menu". Not aria-modal: its close button is the
          // bar's X above it, which aria-modal would hide from VoiceOver.
          // The page behind is made inert instead (effect above), so the bar
          // and the sheet are all a screen reader can reach while it is open.
          <motion.div
            ref={sheetRef}
            id="site-menu-sheet"
            key="sheet"
            role="dialog"
            aria-label={t('immersiveChrome.nav.menu', { defaultValue: 'Menu' })}
            tabIndex={-1}
            variants={reduceMotion ? fadeVariants : sheetVariants}
            initial="closed"
            animate="open"
            exit="exit"
            className="grain fixed inset-0 z-0 h-[100dvh] overflow-y-auto overscroll-contain bg-[#07090d] outline-none xl:hidden"
          >
            {/* Stage light: a warm glow top-right, a cool one bottom-left.
                Softened by the gradient's own stops, not a filter blur
                (2026-09-25): big blurred layers leave WebKit with unpainted
                tiles, and each one costs a blur pass on a phone. */}
            <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
              <div className="absolute -right-32 -top-32 h-[26rem] w-[26rem] bg-[radial-gradient(closest-side,rgba(244,190,82,0.2),rgba(244,190,82,0.08)_45%,transparent)]" />
              <div className="absolute -bottom-40 -left-32 h-[30rem] w-[30rem] bg-[radial-gradient(closest-side,rgba(79,197,205,0.15),rgba(79,197,205,0.06)_45%,transparent)]" />
            </div>

            {/* Phones (2026-09-24): the links sit a little above the middle
                of the room over the language + account block, and their rows
                grow with the screen's height, so a tall phone no longer shows
                four rows at the top and a third of a screen of black under them. */}
            <motion.div
              variants={listVariants}
              className="relative z-10 mx-auto flex min-h-full w-full max-w-xl flex-col px-5 pb-safe pt-[calc(var(--nav-h)_+_0.5rem)] sm:px-8 md:justify-center md:py-24"
            >
              {/* The extra bottom padding lifts the list above the centre on
                  a tall phone, so it hangs from the bar instead of floating
                  in the middle; it shrinks to 1rem on a short one (iPhone SE)
                  so the sheet still fits without scrolling. */}
              <div className="flex flex-1 flex-col justify-center pb-4 pt-3 max-md:supports-[height:1svh]:pb-[max(1rem,calc(18svh_-_6rem))] md:flex-none md:p-0">
                <motion.p variants={sheetItem} className="eyebrow" aria-hidden="true">
                  {t('shell.studios')}
                </motion.p>
                <motion.div variants={sheetItem} className="mt-4">
                  <StudioList dense onPick={() => close(false)} />
                </motion.div>
                <motion.button
                  variants={sheetItem}
                  type="button"
                  onClick={() => {
                    close(false);
                    openCommandMenu();
                  }}
                  className="mt-3 flex h-12 w-full items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.04] px-4 text-left text-[0.95rem] font-semibold text-foreground"
                >
                  <Search className="h-4 w-4 text-primary" aria-hidden="true" />
                  {t('shell.searchLong')}
                </motion.button>

                <motion.p variants={sheetItem} className="eyebrow mt-8" aria-hidden="true">
                  {t('immersiveChrome.nav.menu', { defaultValue: 'Menu' })}
                </motion.p>

                <ul className="mt-5 border-t border-white/[0.08]">
                  {visibleLinks.map((link) => (
                    <motion.li key={`${link.path}${link.hash}`} variants={sheetItem} className="border-b border-white/[0.08]">
                      {hasImage(link) ? (
                        // The picture beside the word, on the same row grid
                        // (index, content, arrow). A picture alone left users
                        // unsure where each row goes.
                        <ImageNavLink
                          link={link}
                          onClick={(e) => close(fromKeyboard(e))}
                          className={`group flex ${ROW_HEIGHT} items-center gap-4 py-2`}
                          imgClassName={SHEET_IMAGE}
                          trail={
                            <>
                              <span className={SHEET_PICTURE_LABEL}>{link.label}</span>
                              <ArrowUpRight
                                aria-hidden="true"
                                className="h-5 w-5 shrink-0 text-muted-foreground transition-transform duration-500 ease-expo-out group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-[#F0A26B]"
                              />
                            </>
                          }
                        />
                      ) : (
                        <Link
                          to={{ pathname: link.path, hash: link.hash }}
                          aria-current={isCurrent(link) ? 'page' : undefined}
                          onClick={(e) => close(fromKeyboard(e))}
                          className={`group flex ${ROW_HEIGHT} items-center gap-4 py-3 ${NAV_TEXT} transition-colors active:text-[#F0A26B] aria-[current=page]:text-[#F0A26B]`}
                        >
                          <span className={SHEET_LABEL}>{link.label}</span>
                          <ArrowUpRight
                            aria-hidden="true"
                            className="h-5 w-5 shrink-0 text-muted-foreground transition-transform duration-500 ease-expo-out group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-[#F0A26B]"
                          />
                        </Link>
                      )}
                    </motion.li>
                  ))}
                  <motion.li variants={sheetItem} className="border-b border-white/[0.08]">
                    <SupportDialog
                      onOpenChange={(open) => {
                        supportOpenRef.current = open;
                      }}
                      trigger={
                        <button
                          type="button"
                          aria-label={t('support.title')}
                          title={t('support.title')}
                          data-testid={SUPPORT_PICTURE.testId}
                          className={`${PICTURE_BUTTON} group flex ${ROW_HEIGHT} w-full items-center gap-4 py-2 text-left`}
                        >
                          <NavImage picture={SUPPORT_PICTURE} className={SHEET_IMAGE} />
                          <span className={SHEET_PICTURE_LABEL}>{t('support.title')}</span>
                          <ArrowUpRight
                            aria-hidden="true"
                            className="h-5 w-5 shrink-0 text-muted-foreground transition-transform duration-500 ease-expo-out group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-[#F0A26B]"
                          />
                        </button>
                      }
                    />
                  </motion.li>
                </ul>
              </div>

              <div className="space-y-5 pt-4 md:mt-14 md:pt-0">
                <motion.div variants={sheetItem} className="flex items-center justify-between gap-3">
                  <span className="text-[12px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                    {t('immersiveChrome.nav.language', { defaultValue: 'Language' })}
                  </span>
                  <LanguageFlagToggle testIdPrefix="menu-lang" />
                </motion.div>

                <motion.div variants={sheetItem} className="grid grid-cols-2 gap-3">
                  {isAuthenticated ? (
                    <>
                      <Link
                        to="/chopshop"
                        onClick={() => close(false)}
                        title={t('navigation.creditBalance')}
                        className="col-span-2 flex h-12 items-center justify-center gap-2 rounded-2xl border border-white/10 bg-white/[0.04] text-[0.95rem] font-semibold text-foreground transition-colors active:bg-white/[0.08]"
                      >
                        <Coins className="h-4 w-4 text-primary" aria-hidden="true" />
                        {creditsLabel}
                      </Link>
                      <Button
                        variant="ghost"
                        className={`h-12 rounded-2xl border border-white/10 text-[0.95rem] ${NAV_TEXT} hover:bg-white/[0.06]`}
                        onClick={(e) => {
                          logout();
                          close(fromKeyboard(e));
                        }}
                      >
                        <LogOut aria-hidden="true" />
                        {t('auth.logout')}
                      </Button>
                      <Link
                        to="/pricing"
                        onClick={() => close(false)}
                        className="flex h-12 items-center justify-center gap-1.5 rounded-2xl bg-[linear-gradient(135deg,#F9D27E,#F4BE52_45%,#E4A43A)] text-[0.95rem] font-bold text-[#10131a] shadow-[0_10px_30px_-10px_rgba(244,190,82,0.7),inset_0_1px_0_rgba(255,255,255,0.45)] transition-transform active:scale-[0.98]"
                      >
                        {t('navigation.buyCreditsCta')}
                        <ArrowRight className="h-4 w-4" aria-hidden="true" />
                      </Link>
                    </>
                  ) : (
                    <>
                      {/* Google first, full width (2026-09-25). The sheet
                          closes once the popup is up, like any other row.
                          A tap before the SDK finished loading (the icon in
                          the bar is display:none on a phone, so the sheet
                          starts the warm-up) keeps it open until the
                          sign-in succeeds: a blocked popup's "tap again"
                          toast needs this button still on screen. */}
                      <GoogleSignInButton
                        source="navbar_menu"
                        onBeforePopup={() => close(false)}
                        onSignedIn={() => close(false)}
                        className="col-span-2 h-12 rounded-2xl text-[0.95rem]"
                      />
                      <Link
                        to="/login"
                        onClick={() => close(false)}
                        className={`flex h-12 items-center justify-center gap-2 rounded-2xl border border-white/[0.12] bg-white/[0.04] text-[0.95rem] font-semibold ${NAV_TEXT} transition-colors active:bg-white/[0.08]`}
                      >
                        <LogIn className="h-4 w-4" aria-hidden="true" />
                        {t('auth.login')}
                      </Link>
                      <Link
                        to="/signup"
                        onClick={() => close(false)}
                        className="flex h-12 items-center justify-center gap-1.5 rounded-2xl bg-[linear-gradient(135deg,#F9D27E,#F4BE52_45%,#E4A43A)] text-[0.95rem] font-bold text-[#10131a] shadow-[0_10px_30px_-10px_rgba(244,190,82,0.7),inset_0_1px_0_rgba(255,255,255,0.45)] transition-transform active:scale-[0.98]"
                      >
                        {t('auth.signup')}
                        <ArrowRight className="h-4 w-4" aria-hidden="true" />
                      </Link>
                    </>
                  )}
                </motion.div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}

